import GolfIcon from '@/components/GolfIcon';
import { FriendRequestAcceptedNotification } from '@/components/notifications/FriendRequestAcceptedNotification';
import { FriendRequestNotification } from '@/components/notifications/FriendRequestNotification';
import { NewPostNotification } from '@/components/notifications/NewPostNotification';
import { PostCommentNotification } from '@/components/notifications/PostCommentNotification';
import { PostLikeNotification } from '@/components/notifications/PostLikeNotification';
import { useUserProfile } from '@/context/UserProfileContext';
import { db } from '@/firebase';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar
} from '@ionic/react';
import { get, onValue, ref, remove, update } from 'firebase/database';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

export type Notification = {
  id: string;
  type: string;
  data: any;
  read: boolean;
  createdAt: number;
  senderInfo: {
    name: string;
    username: string;
    avatar: string;
  }[];
};

const NotificationsPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { profile } = useUserProfile();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const userId = profile?.userId;

  document.title = t('notifications');

  useEffect(() => {
    if (!userId) return;

    const notificationRef = ref(db, `notifications/${userId}`);
    const unsubscribe = onValue(notificationRef, async (snapshot) => {
      const data = snapshot.val();
      if (data) {
        // Format notifications
        const formattedNotifications = Object.entries(data).reverse().map(([id, value]: any) => ({
          id,
          ...value,
        }));
        const notificationsWithUserInfo = await Promise.all(
          formattedNotifications.map(async (notification) => {
            const senderIds = notification.data.senderIds || [notification.data.senderId];
            const senderInfoPromises = senderIds.map(async (senderId: string) => {
              const senderRef = ref(db, `users/${senderId}`);
              const senderSnapshot = await get(senderRef);
              return senderSnapshot.exists() ? senderSnapshot.val() : null;
            });
            const senderInfo = (await Promise.all(senderInfoPromises)).filter(Boolean);
            return {
              ...notification,
              senderInfo,
            };
          }),
        );
        setNotifications(notificationsWithUserInfo.sort((a, b) => b.createdAt - a.createdAt));

        // Mark all as read when viewing the notifications page
        markAllAsRead();
      } else {
        setNotifications([]);
      }
    });

    return () => unsubscribe();
  }, [userId]);

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!userId) return;

    try {
      const updates: Record<string, any> = {};
      notifications.forEach((notification) => {
        if (!notification.read) {
          updates[`/notifications/${userId}/${notification.id}/read`] = true;
        }
      });

      if (Object.keys(updates).length > 0) {
        await update(ref(db), updates);
        setNotifications(prev =>
          prev.map(notification => ({ ...notification, read: true })),
        );
      }
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  const openSettings = () => {
    history.push('/settings/notifications');
  };

  const handleDeleteNotification = async (notificationId: string) => {
    if (!userId) return;

    try {
      const notificationRef = ref(db, `notifications/${userId}/${notificationId}`);
      await remove(notificationRef);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  return (
    <IonPage className="mx-auto max-w-xl">
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonTitle>{t('notifications')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={openSettings} fill="clear">
              <GolfIcon name="settings" className="size-5" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {notifications.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center">
            <GolfIcon name="notification" className="mb-4 size-16" />
            <p className="text-muted-foreground">{t('no_notifications')}</p>
          </div>
        ) : (
          <ul>
            {notifications.map((notification) => {
              switch (notification.type) {
                case 'friend_request':
                  return (
                    <li key={notification.id} className="flex w-full items-center px-2 justify-between border-b">
                      <div className="flex items-center space-x-4">
                        <FriendRequestNotification
                          userId={notification.data.senderId!}
                          friendshipId={notification.data.friendshipId}
                          senderInfo={notification.senderInfo[0]!}
                        />
                      </div>

                      <IonButton
                        fill="clear"
                        className='hidden'
                        onClick={() => handleDeleteNotification(notification.id)}
                      >
                        {t('delete')}
                      </IonButton>
                    </li>
                  );
                case 'friend_request_accepted':
                  return (
                    <li key={notification.id} className="flex w-full items-center px-2 justify-between border-b">
                      <div className="flex items-center space-x-4">
                        <FriendRequestAcceptedNotification
                          userId={notification.data.senderId!}
                          senderInfo={notification.senderInfo[0]!}
                        />
                      </div>
                      <IonButton
                        fill="clear"
                        className='hidden'
                        onClick={() => handleDeleteNotification(notification.id)}
                      >
                        {t('delete')}
                      </IonButton>
                    </li>
                  );
                case 'new_post':
                  return (
                    <li key={notification.id} className="flex w-full items-center px-2 justify-between border-b">
                      <div className="flex w-full items-center space-x-4">
                        <NewPostNotification
                          postData={notification.data}
                          senderInfo={notification.senderInfo[0]!}
                        />
                      </div>
                      <IonButton
                        fill="clear"
                        className='hidden'
                        onClick={() => handleDeleteNotification(notification.id)}
                      >
                        {t('delete')}
                      </IonButton>
                    </li>
                  );
                case 'post_like':
                  return (
                    <li key={notification.id} className="flex w-full items-center px-2 justify-between border-b">
                      <div className="flex w-full items-center space-x-4">
                        <PostLikeNotification
                          senderInfo={notification.senderInfo}
                          likesData={notification.data}
                        />
                      </div>
                      <IonButton
                        fill="clear"
                        onClick={() => handleDeleteNotification(notification.id)}
                      >
                        {t('delete')}
                      </IonButton>
                    </li>
                  );
                case 'post_comment':
                  return (
                    <li key={notification.id} className="flex w-full items-center px-2 justify-between border-b">
                      <div className="flex w-full items-center space-x-4">
                        <PostCommentNotification
                          senderInfo={notification.senderInfo}
                          commentData={notification.data}
                        />
                      </div>
                      <IonButton
                        fill="clear"
                        className='hidden'
                        onClick={() => handleDeleteNotification(notification.id)}
                      >
                        {t('delete')}
                      </IonButton>
                    </li>
                  );
                default:
                  return null;
              }
            })}
          </ul>
        )}
      </IonContent>
    </IonPage>
  );
};

export default NotificationsPage;
